import { useMemo, useState } from "react";
import { Button, Footer } from "risa-oasis-ui_v2";
import { OrderAssignmentConfiguration as OrderAssignmentConfigType } from "../../../../api/services/orderAssignmentConfigService";
import BadgeDropdown from "../../../../components/badgeDropdown/badgeDropdown";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import { SELECT_A_HEALTH_CARE_FACILITY } from "../../../../constants/stringLiterals";
import { BadgeWithIconProps } from "../../../../data-model/badgeWithIconProps";
import { useHealthCareFacilities } from "../../../../hooks/useHealthCareFacilities";
import AddProviderModal from "./components/AddProviderModal";
import EditConfigurationModal from "./components/EditConfigurationModal";
import { useOrderAssignmentConfigurations } from "./hooks/useOrderAssignmentConfigurations";
import {
  convertFacilityOptionsToBadges,
  getSelectedFacilityBadge,
} from "./utils/orderAssignmentHelpers";
import { OrderAssignmentTableHeaders } from "./utils/orderAssignmentTable";

const OrderAssignmentConfiguration = () => {
  const { healthCareFacilityOptions } = useHealthCareFacilities();
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddProviderModalOpen, setIsAddProviderModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] =
    useState<OrderAssignmentConfigType | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Track current table page start so row updates use the correct config (CustomTable passes page-local rowIndex)
  const [tableStartIndex, setTableStartIndex] = useState<number>(0);

  // Convert facility options to badge format
  const facilityBadgeOptions: BadgeWithIconProps[] = useMemo(
    () => convertFacilityOptionsToBadges(healthCareFacilityOptions),
    [healthCareFacilityOptions],
  );

  // Find the selected facility badge
  const selectedFacilityBadge: BadgeWithIconProps | null = useMemo(
    () =>
      getSelectedFacilityBadge(
        selectedFacilityId,
        facilityBadgeOptions,
        SELECT_A_HEALTH_CARE_FACILITY,
      ),
    [selectedFacilityId, facilityBadgeOptions],
  );

  /**
   * Handle edit icon click - opens the edit modal
   */
  const handleEditClick = (config: OrderAssignmentConfigType) => {
    setSelectedConfig(config);
    setIsEditModalOpen(true);
  };

  // Use the custom hook for configuration management
  const {
    configurations,
    loading,
    saving,
    error,
    hasUnsavedChanges,
    loadConfigurations,
    handleSave,
    handleDiscard,
    handleToggleChange,
    handleTableRowUpdate,
    handleConfigurationUpdate,
    tableData,
    filteredConfigurationCount,
  } = useOrderAssignmentConfigurations({
    selectedFacilityId,
    tableStartIndex,
    searchTerm,
    onEdit: handleEditClick,
  });

  /**
   * Handle saving configuration from the edit modal - updates local state only
   */
  const handleSaveConfiguration = (
    updatedConfig: OrderAssignmentConfigType,
  ) => {
    if (!updatedConfig.id) return;

    handleConfigurationUpdate(updatedConfig.id, {
      auth_status: updatedConfig.auth_status,
      payer_patterns: updatedConfig.payer_patterns,
    });

    setIsEditModalOpen(false);
    setSelectedConfig(null);
  };

  /**
   * Handle facility change with unsaved changes confirmation
   */
  const handleFacilityChange = (badgeId: string) => {
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        "You have unsaved changes. Are you sure you want to switch facilities?",
      );
      if (!confirmDiscard) return;
    }
    setSelectedFacilityId(badgeId);
  };

  return (
    <div className="order-assignment-configuration flex h-full flex-col gap-4 overflow-hidden p-2">
      <div className="order-assignment-configuration__header flex items-center gap-4">
        <h1 className="order-assignment-configuration__header-text text-lg font-bold not-italic leading-normal text-black">
          Auto Assignment
        </h1>
        <div className="order-assignment-configuration__header action">
          <BadgeDropdown
            selectedBadge={selectedFacilityBadge}
            badgeList={facilityBadgeOptions}
            onClick={handleFacilityChange}
            shouldUsePortal={true}
          />
        </div>
      </div>

      <div className="order-assignment-configuration__content flex flex-1 flex-col gap-4 overflow-y-auto">
        {!selectedFacilityId ? (
          <div className="flex h-40 items-center justify-center text-center text-sm text-gray-500">
            Please select a facility to view order assignment configurations.
          </div>
        ) : loading ? (
          <div className="flex h-full w-full items-center justify-center">
            <LoaderMessage message="Loading order assignment configurations..." />
          </div>
        ) : error ? (
          <div className="flex h-40 items-center justify-center text-center text-sm text-red-500">
            Error: {error}
          </div>
        ) : configurations.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-6">
            <div className="flex flex-col items-center gap-4">
              <span className="text-center text-sm font-normal leading-5 text-gray-600">
                No order assignment configurations found for this facility.
              </span>
              <Button
                buttonType="primary"
                size="medium"
                onClick={() => setIsAddProviderModalOpen(true)}
                disabled={false}
              >
                Add Provider
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <CustomTable
              tableHeaders={OrderAssignmentTableHeaders}
              tableData={tableData}
              tableName="orderAssignmentConfiguration"
              itemsPerPage={12}
              pagesPerView={4}
              count={filteredConfigurationCount}
              totalCount={filteredConfigurationCount}
              startIndexOfTable={setTableStartIndex}
              endIndexOfTable={() => {}}
              searchingText={setSearchTerm}
              hideSearchBar={false}
              isFetching={false}
              showExportButton={false}
              showInLineLoader={false}
              showRefreshButton={true}
              hidePagination={false}
              onRefreshButton={loadConfigurations}
              placeholder="Search by name"
              onChangeEmit={handleToggleChange}
              onTableRowUpdate={handleTableRowUpdate}
              showOtherButton={true}
              otherButtonMeta={{
                text: "Add Provider",
                onClick: () => setIsAddProviderModalOpen(true),
                disabled: false,
                buttonType: "primary",
                size: "medium",
              }}
            />
          </div>
        )}
      </div>
      {hasUnsavedChanges && (
        <div className="order-assignment-configuration__footer">
          <Footer
            cancelButtonText="Go Back"
            submitButtonText={saving ? "Saving..." : "Save Changes"}
            onCancelClick={handleDiscard}
            onSubmitClick={handleSave}
            isDisabled={saving}
          />
        </div>
      )}
      {/* Edit Configuration Modal */}
      <EditConfigurationModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedConfig(null);
        }}
        onSave={handleSaveConfiguration}
        config={selectedConfig}
        facilityId={selectedFacilityId}
        onOrgChange={() => {
          // Reload configurations after org change
          loadConfigurations();
        }}
      />

      {/* Add Provider Modal */}
      <AddProviderModal
        isOpen={isAddProviderModalOpen}
        onClose={() => setIsAddProviderModalOpen(false)}
        facilityId={selectedFacilityId}
        onProviderAdded={() => {
          // Reload configurations after adding provider
          loadConfigurations();
        }}
      />
    </div>
  );
};

export default OrderAssignmentConfiguration;
